import * as React from 'react';
import { Header } from 'semantic-ui-react';

import { clienteApi, Resultados } from 'src/api';
import { Contexto } from 'src/contexto';
import { ModalCorreos } from '../ModalCorreos';
import { imprimirFormaDePago } from '../utilidades';
import MenuTabla from './componentes/MenuTabla';
import TablaRecordatorios from './componentes/TablaRecordatorios';
import { FiltroEsDeudor, FiltroSede } from './tipos';

interface Props {
    readonly ocultar: boolean;
}
interface State {
    readonly buscando: boolean;
    readonly diaSeleccionado: number;
    readonly formaDePagoSeleccionada: 'cheque' | 'pagare';
    readonly filtroDeudoresSeleccionado: FiltroEsDeudor;
    readonly añoSeleccionado: number | 'todos';
    readonly sedeSeleccionada: FiltroSede;
    readonly recordatoriosDePago: Resultados<'recordatoriosDePago'>;
    readonly mostrandoCorreos: boolean;
}

class RecordatoriosDePago extends React.Component<Props, State> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public state: State = {
        buscando: false,
        diaSeleccionado: 5,
        formaDePagoSeleccionada: 'pagare',
        filtroDeudoresSeleccionado: 'todos',
        añoSeleccionado: 'todos',
        sedeSeleccionada: 'todos',
        recordatoriosDePago: [],
        mostrandoCorreos: false,
    };

    public componentDidMount() {
        if (!this.props.ocultar) {
            this.obtenerRecordatoriosDePago(this.state.diaSeleccionado, 'pagare', 'todos');
        }
    }

    public componentDidUpdate(prevProps: Props) {
        if (!this.props.ocultar && prevProps.ocultar) {
            const {
                diaSeleccionado,
                formaDePagoSeleccionada,
                filtroDeudoresSeleccionado,
            } = this.state;
            this.obtenerRecordatoriosDePago(
                diaSeleccionado,
                formaDePagoSeleccionada,
                filtroDeudoresSeleccionado,
            );
        }
    }

    public render() {
        if (this.props.ocultar) return null;
        const {
            buscando,
            diaSeleccionado,
            formaDePagoSeleccionada,
            filtroDeudoresSeleccionado,
            recordatoriosDePago,
            mostrandoCorreos,
            añoSeleccionado,
            sedeSeleccionada,
        } = this.state;

        return (
            <div>
                {mostrandoCorreos && this.renderizarModalCorreos()}

                <Header as='h1'>Días de pago</Header>
                <MenuTabla
                    cantidadDeRecordatorios={recordatoriosDePago.length}
                    diaDePago={diaSeleccionado}
                    formaDePago={formaDePagoSeleccionada}
                    filtroDeudores={filtroDeudoresSeleccionado}
                    año={añoSeleccionado}
                    sede={sedeSeleccionada}
                    alElegirDiaDePago={this.cambiarDiaSeleccionado}
                    alElegirFormaDePago={this.cambiarFormaDePago}
                    alElegirFiltroDeudores={this.cambiarFiltroDeudores}
                    alElegirAño={this.cambiarAñoSeleccionado}
                    alElegirSede={this.cambiarSedeSeleccionada}
                    cargando={buscando}
                    alPresionarRecargar={() =>
                        this.obtenerRecordatoriosDePago(
                            diaSeleccionado,
                            formaDePagoSeleccionada,
                            filtroDeudoresSeleccionado,
                        )
                    }
                    alPresionarCorreos={() => this.setState({ mostrandoCorreos: true })}
                />
                {this.renderizarTabla()}
            </div>
        );
    }

    private renderizarTabla() {
        const { recordatoriosDePago } = this.state;

        return <TablaRecordatorios recordatoriosDePago={recordatoriosDePago} />;
    }

    private renderizarModalCorreos() {
        const { recordatoriosDePago, formaDePagoSeleccionada } = this.state;
        const correos = recordatoriosDePago.map(r => ({ año: r.paraElAño, correo: r.correo }));

        return (
            <ModalCorreos
                correos={correos}
                alCerrar={() => this.setState({ mostrandoCorreos: false })}
            >
                <p>
                    Mostrando solo correos de apoderados con forma de pago{' '}
                    <b>{imprimirFormaDePago(formaDePagoSeleccionada).toLocaleLowerCase()}</b>.
                </p>
            </ModalCorreos>
        );
    }

    private readonly obtenerRecordatoriosDePago = (
        diaDePago: number,
        formaDePago: 'cheque' | 'pagare',
        filtroDeudores: FiltroEsDeudor,
        año: number | 'todos' = this.state.añoSeleccionado,
        sede: FiltroSede = this.state.sedeSeleccionada,
    ) => {
        this.setState({ buscando: true });

        clienteApi
            .ejecutar('recordatoriosDePago', {
                diaDePago,
                formaDePago,
                filtro: filtroDeudores,
                año,
                sede,
            })
            .then(recordatoriosDePago => {
                const recordatoriosOrdenados = recordatoriosDePago.sort((a, b) => {
                    return (
                        a.nombre.primerNombre.localeCompare(b.nombre.primerNombre) ||
                        a.nombre.nombresSecundarios.localeCompare(b.nombre.nombresSecundarios) ||
                        a.nombre.apellidoPaterno.localeCompare(b.nombre.apellidoPaterno) ||
                        a.nombre.apellidoMaterno.localeCompare(b.nombre.apellidoMaterno)
                    );
                });
                this.setState({ buscando: false, recordatoriosDePago: recordatoriosOrdenados });
            })
            .catch(error => {
                this.setState({ buscando: false });
                this.context.mostrarModalError(error);
            });
    };

    private readonly cambiarDiaSeleccionado = (dia: number) => {
        this.setState({ diaSeleccionado: dia });
        this.obtenerRecordatoriosDePago(
            dia,
            this.state.formaDePagoSeleccionada,
            this.state.filtroDeudoresSeleccionado,
        );
    };

    private readonly cambiarFormaDePago = (formaDePago: 'cheque' | 'pagare') => {
        this.setState({ formaDePagoSeleccionada: formaDePago });
        this.obtenerRecordatoriosDePago(
            this.state.diaSeleccionado,
            formaDePago,
            this.state.filtroDeudoresSeleccionado,
        );
    };

    private readonly cambiarFiltroDeudores = (filtroDeudores: FiltroEsDeudor) => {
        this.setState({ filtroDeudoresSeleccionado: filtroDeudores });
        this.obtenerRecordatoriosDePago(
            this.state.diaSeleccionado,
            this.state.formaDePagoSeleccionada,
            filtroDeudores,
        );
    };

    private readonly cambiarAñoSeleccionado = (año: number | 'todos') => {
        this.setState({ añoSeleccionado: año });
        this.obtenerRecordatoriosDePago(
            this.state.diaSeleccionado,
            this.state.formaDePagoSeleccionada,
            this.state.filtroDeudoresSeleccionado,
            año,
        );
    };

    private readonly cambiarSedeSeleccionada = (sede: FiltroSede) => {
        this.setState({ sedeSeleccionada: sede });
        this.obtenerRecordatoriosDePago(
            this.state.diaSeleccionado,
            this.state.formaDePagoSeleccionada,
            this.state.filtroDeudoresSeleccionado,
            this.state.añoSeleccionado,
            sede,
        );
    };
}

export default RecordatoriosDePago;



// WEBPACK FOOTER //
// ./src/modulos/Cobranzas/componentes/RecordatoriosDePago/index.tsx